import { Button } from '~/components/common/Button'

import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { HeaderGradient } from '~/components/common/HeaderGradient'
import {
  LabeledMultipleCurieSelect,
  SelectGroupType
} from '~/components/common/LabeledMultipleSelect'
import RadioGroup from '~/components/common/RadioGroup'
import {
  AgentCommands,
  areRequiredFieldsNotSatisfied,
  CodeContextGroup,
  CommandType,
  getRequiredFieldsErrorMessage,
  isMultiRepoRequired,
  isRepoNotRequired,
  QuestionAnsweringCommands,
  UsedCommandType
} from '~/constants/commands'
import { CaretLeftIcon } from '~/constants/icon'
import { DefaultVersion, JavaVersion, MavenVersion, MuleRuntimeVersion } from '~/constants/settings'
import { useAgentUnavailable } from '~/contexts/AppConfigContext'
import { useIndexesContext } from '~/contexts/IndexesContext'
import { useSidebarContext } from '~/contexts/SidebarContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import {
  getCommandGroup,
  resolveCommandFromOption,
  shouldUpdateOption
} from '~/hooks/task/useDefaultCommand'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { useRepoMetadata } from '~/hooks/task/useRepoMetadata'
import { useCodeContext } from '~/hooks/useCodeContext'
import { useTaskFormDraft } from '~/hooks/useTaskFormDraft'
import { useTracer } from '~/hooks/useTracer'
import {
  AgentRequest,
  CodeContext,
  Command,
  ExecutionMode,
  IntegrationPlatform,
  Metadata,
  RuntimeSettings,
  SourceOutputExampleType,
  sourceOutputExampleTypeFromJSON,
  sourceOutputExampleTypeToJSON,
  VerificationMode
} from '~/protos/agents'
import { readFileAsText } from '~/services/onboardingService'
import { projectService } from '~/services/plugin/projectService'
import { parseRepositoryName } from '~/utils/repositoryUtils'
import { resolveVmArgs } from '~/utils/runtimeSettingsPriority'
// Removed submitTaskUpdate import - now handled by parent component
import { Checkbox } from '@nextui-org/react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import IntegrationTestFlowScreen from '~/components/task-forms/IntegrationTestFlowScreen'
import {
  completeRepositorySelection,
  resetFlow,
  selectCurrentStep,
  selectIsInFlow,
  startFlow
} from '~/store/integrationTestFlowSlice'
import { AppEvents, globalEventEmitter } from '~/utils/shared'
import { BetterToast } from '~/utils/toast'
import { cm } from '~/utils/utils'
import MuleProjectUpload from '../campaign/MuleProjectUpload'
import { CodeContextSelector } from '../editors/CodeContextSelector'
import FlowNameSelector from '../common/FlowNameSelector'
import { BaseTaskForm, TaskParams } from '~/components/task-forms/BaseTaskForm'
import { BillableMuleFlowForm } from '~/components/task-forms/BillableMuleFlowForm'
import { CodeEnhancerForm } from '~/components/task-forms/CodeEnhancerForm'
import { DiagramGeneratorForm } from '~/components/task-forms/DiagramGeneratorForm'
import {
  DOCUMENT_GENERATOR_MAX_FLOWS,
  FlowDocsGeneratorForm
} from '~/components/task-forms/FlowDocsGeneratorForm'
import { GenerateMigrationDocForm } from '~/components/task-forms/GenerateMigrationDocForm'
import { InputOutputToDWLForm } from '~/components/task-forms/InputOutputToDWLForm'
import { IntegrationGeneratorForm } from '~/components/task-forms/shared/non-skill-forms/IntegrationGeneratorForm'
import IntegrationRepoSelectionModal from '~/components/task-forms/shared/IntegrationRepoSelectionModal'
import { MappingTableGeneratorForm } from '~/components/task-forms/MappingTableGeneratorForm'
import { MappingTableToDWLForm } from '~/components/task-forms/shared/non-skill-forms/MappingTableToDWLForm'
import { MigrationAcceleratorForm } from '~/components/task-forms/MigrationAcceleratorForm'
import { Mule3ToMule4MigrationForm } from '~/components/task-forms/shared/non-skill-forms/Mule3ToMule4MigrationForm'
import { MulesoftToCamelForm } from '~/components/task-forms/shared/non-skill-forms/MulesoftToCamelForm'
import { MuleSoftToNodeJsForm } from '~/components/task-forms/shared/non-skill-forms/MuleSoftToNodeJsForm'
import { MuleVersionUpgradeForm } from '~/components/task-forms/MuleVersionUpgradeForm'
import { MunitTestForm } from '~/components/task-forms/MunitTestForm'
import { SampleDataGeneratorForm } from '~/components/task-forms/SampleDataGeneratorForm'
import Typography from '../common/Typography'
import { bridge } from '~/services/bridge'
import { usePluginWorkspaceSafe } from '~/contexts/PluginWorkspaceContext'

// Type definitions
export type ProjectSelectionOption = 'none' | 'upload' | 'codeContext' | 'mappingTable'
type UploadSourceOption = 'computer' | 'ide'

interface RuntimeSelectionProps {
  taskRuntimeSettings: Record<string, string | undefined>
  setTaskRuntimeSettings: (settings: Record<string, string | undefined>) => void
  task: TaskParams | null
  setTask: (task: TaskParams | null) => void
  label?: React.ReactNode
  showSettingsXML?: boolean
  showMuleRuntime?: boolean
  disabled?: boolean
  isLoading?: boolean
}

function RuntimeSelection({
  taskRuntimeSettings,
  setTaskRuntimeSettings,
  task,
  setTask,
  label = (
    <>
      Runtime Settings<span className='text-tokens-status-error-text'> *</span>
    </>
  ),
  showSettingsXML = true,
  showMuleRuntime = true,
  disabled = false,
  isLoading = false
}: RuntimeSelectionProps) {
  const groups: SelectGroupType[] = [
    {
      title: (
        <>
          Java Version <span className='text-tokens-status-error-text'>*</span>
        </>
      ),
      key: 'javaVersion',
      items: Object.entries(JavaVersion).map(([, value]) => ({
        value: `${value}`,
        label: `Java ${value}`
      }))
    },
    {
      title: (
        <>
          Maven Version <span className='text-tokens-status-error-text'>*</span>
        </>
      ),
      key: 'mavenVersion',
      items: Object.entries(MavenVersion).map(([, value]) => ({
        value: `${value}`,
        label: `Maven ${value}`
      }))
    }
  ]

  if (showMuleRuntime) {
    groups.push({
      title: (
        <>
          Mule Version <span className='text-tokens-status-error-text'>*</span>
        </>
      ),
      key: 'muleRuntimeVersion',
      items: Object.entries(MuleRuntimeVersion).map(([, value]) => ({
        value: `${value}`,
        label: `Mule ${value}`
      }))
    })
  }

  // Function to handle removing settings XML
  const handleRemoveSettingsXML = (isSettingsSecurityXML = false) => {
    try {
      const updatedSettings: Record<string, string> = {
        ...taskRuntimeSettings,
        settingsXmlRawTxt: isSettingsSecurityXML
          ? (taskRuntimeSettings.settingsXmlRawTxt ?? '')
          : '',
        settingsSecurityXmlRawTxt: ''
      }

      setTaskRuntimeSettings(updatedSettings)

      if (task) {
        const request = AgentRequest.fromPartial({
          ...task.request,
          runtimeSettings: RuntimeSettings.fromPartial({
            javaVersion: updatedSettings.javaVersion,
            mavenVersion: updatedSettings.mavenVersion,
            muleRuntimeVersion: showMuleRuntime ? updatedSettings.muleRuntimeVersion : '',
            settingsXmlRawTxt: updatedSettings.settingsXmlRawTxt,
            settingsSecurityXmlRawTxt: updatedSettings.settingsSecurityXmlRawTxt
          })
        })
        setTask({ request })
      }

      BetterToast.success('Settings XML removed successfully')
    } catch (error) {
      BetterToast.error('Failed to remove Settings XML')
      console.error('Error removing settings XML:', error)
    }
  }

  // Function to handle uploading settings XML
  const handleUploadSettingsXML = (isSettingsSecurityXML = false) => {
    // Create a file input element
    const fileInput = document.createElement('input')
    fileInput.type = 'file'
    fileInput.accept = '.xml'

    // Handle file selection
    fileInput.onchange = async e => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        try {
          // Read the XML file as text
          const xmlContent = await readFileAsText(file)

          // Update the runtime settings with the XML content
          const updatedSettings: Record<string, string> = {
            ...taskRuntimeSettings,
            settingsXmlRawTxt: isSettingsSecurityXML
              ? (taskRuntimeSettings.settingsXmlRawTxt ?? '')
              : xmlContent,
            settingsSecurityXmlRawTxt: isSettingsSecurityXML
              ? xmlContent
              : (taskRuntimeSettings.settingsSecurityXmlRawTxt ?? '')
          }

          setTaskRuntimeSettings(updatedSettings)

          if (task) {
            const request = AgentRequest.fromPartial({
              ...task.request,
              runtimeSettings: RuntimeSettings.fromPartial({
                javaVersion: updatedSettings.javaVersion,
                mavenVersion: updatedSettings.mavenVersion,
                muleRuntimeVersion: showMuleRuntime ? updatedSettings.muleRuntimeVersion : '',
                settingsXmlRawTxt: updatedSettings.settingsXmlRawTxt,
                settingsSecurityXmlRawTxt: updatedSettings.settingsSecurityXmlRawTxt
              })
            })
            setTask({ request })
          }

          BetterToast.success(
            `${isSettingsSecurityXML ? 'Settings Security' : 'Settings'} XML uploaded successfully`
          )
        } catch (error) {
          BetterToast.error(
            `Failed to upload ${isSettingsSecurityXML ? 'Settings Security' : 'Settings'} XML`
          )
          console.error(
            `Error uploading ${isSettingsSecurityXML ? 'settings security' : 'settings'} XML:`,
            error
          )
        }
      }
    }

    // Trigger the file dialog
    fileInput.click()
  }

  // Function to truncate XML content for display
  const truncateXML = (xml: string, maxLength = 100) => {
    if (!xml) return ''
    if (xml.length <= maxLength) return xml
    return xml.substring(0, maxLength) + '...'
  }

  const hasSettingsXML = !!taskRuntimeSettings.settingsXmlRawTxt
  const hasSettingsSecurityXML = !!taskRuntimeSettings.settingsSecurityXmlRawTxt

  return (
    <div className='flex flex-col'>
      <LabeledMultipleCurieSelect
        groups={groups}
        isMultiSelect={true}
        selectedKeys={{
          javaVersion: taskRuntimeSettings.javaVersion,
          mavenVersion: taskRuntimeSettings.mavenVersion,
          muleRuntimeVersion: taskRuntimeSettings.muleRuntimeVersion
        }}
        onSelectionChange={keys => {
          if (disabled) return
          const updatedSettings = {
            ...taskRuntimeSettings,
            ...keys
          }
          setTaskRuntimeSettings(updatedSettings)
          if (task) {
            const request = AgentRequest.fromPartial({
              ...task.request,
              runtimeSettings: RuntimeSettings.fromPartial({
                javaVersion: updatedSettings.javaVersion,
                mavenVersion: updatedSettings.mavenVersion,
                muleRuntimeVersion: showMuleRuntime ? updatedSettings.muleRuntimeVersion : '',
                settingsXmlRawTxt: updatedSettings.settingsXmlRawTxt,
                settingsSecurityXmlRawTxt: updatedSettings.settingsSecurityXmlRawTxt
              })
            })
            setTask({ request })
          }
        }}
        label={label}
        isDisabled={disabled}
        isLoading={isLoading}
      />

      {/* Settings XML Section - only show for target runtime */}
      {showSettingsXML && (
        <>
          <div className='mt-2'>
            {hasSettingsXML ? (
              <div className='mt-1'>
                <div className='flex items-center justify-between bg-gray-50 p-2 rounded-md border border-gray-200'>
                  <div className='flex-1 overflow-hidden'>
                    <div className='text-xs font-medium select-none'>settings.xml</div>
                    <div className='text-xs text-gray-600 font-mono overflow-hidden text-ellipsis select-none'>
                      {truncateXML(taskRuntimeSettings.settingsXmlRawTxt ?? '')}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveSettingsXML()}
                    className='ml-2 p-1 text-gray-600 hover:text-gray-800'
                    aria-label='Remove settings XML'
                  >
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z'
                        fill='currentColor'
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => !disabled && handleUploadSettingsXML()}
                className='inline-flex h-7 items-center justify-center gap-1 px-2 py-1 mt-1 select-none'
                disabled={disabled}
                style={{
                  opacity: disabled ? 0.5 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer'
                }}
              >
                <svg
                  width='12'
                  height='12'
                  viewBox='0 0 18 18'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  className='mt-[-1px]'
                >
                  <path
                    d='M8.25 9.75H3.75V8.25H8.25V3.75H9.75V8.25H14.25V9.75H9.75V14.25H8.25V9.75Z'
                    fill='#6750A4'
                  />
                </svg>
                <span
                  style={{
                    color: '#6750A4',
                    fontSize: '12px',
                    fontWeight: '500',
                    lineHeight: '16px',
                    letterSpacing: '0.1px'
                  }}
                >
                  Upload Settings XML
                </span>
              </button>
            )}
          </div>

          {hasSettingsXML ? (
            <div className='mt-2'>
              {hasSettingsSecurityXML ? (
                <div className='mt-1'>
                  <div className='flex items-center justify-between bg-gray-50 p-2 rounded-md border border-gray-200'>
                    <div className='flex-1 overflow-hidden'>
                      <div className='text-xs font-medium select-none'>settings-security.xml</div>
                      <div className='text-xs text-gray-600 font-mono overflow-hidden text-ellipsis select-none'>
                        {truncateXML(taskRuntimeSettings.settingsSecurityXmlRawTxt ?? '')}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveSettingsXML(true)}
                      className='ml-2 p-1 text-gray-600 hover:text-gray-800'
                      aria-label='Remove settings XML'
                    >
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 24 24'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z'
                          fill='currentColor'
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => !disabled && handleUploadSettingsXML(true)}
                  className='inline-flex h-7 items-center justify-center gap-1 px-2 py-1 mt-1 select-none'
                  disabled={disabled}
                  style={{
                    opacity: disabled ? 0.5 : 1,
                    cursor: disabled ? 'not-allowed' : 'pointer'
                  }}
                >
                  <svg
                    width='12'
                    height='12'
                    viewBox='0 0 18 18'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                    className='mt-[-1px]'
                  >
                    <path
                      d='M8.25 9.75H3.75V8.25H8.25V3.75H9.75V8.25H14.25V9.75H9.75V14.25H8.25V9.75Z'
                      fill='#6750A4'
                    />
                  </svg>
                  <span
                    style={{
                      color: '#6750A4',
                      fontSize: '12px',
                      fontWeight: '500',
                      lineHeight: '16px',
                      letterSpacing: '0.1px'
                    }}
                  >
                    Upload Settings Security XML
                  </span>
                </button>
              )}
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}

export interface TaskFormProps {
  command?: UsedCommandType
  withExamplePrefilled?: boolean
  className?: string
  closeTask: () => void
  initialTaskData?: TaskParams | null
  isEditingTask?: boolean
  onTaskSubmit: (taskParams: TaskParams) => Promise<boolean> // Required, not optional
}

function normalizeRepositoryCoderPlatform(platform?: IntegrationPlatform) {
  if (
    !platform ||
    platform === IntegrationPlatform.UNKNOW_INTEGRATION_PLATFORM ||
    platform === IntegrationPlatform.UNRECOGNIZED
  ) {
    return IntegrationPlatform.MULESOFT
  }
  return platform
}

function isRepositoryCoderJvmPlatform(platform?: IntegrationPlatform) {
  return platform === IntegrationPlatform.JAVA_SPRING_BOOT || platform === IntegrationPlatform.CAMEL
}

export function TaskForm({
  command,
  withExamplePrefilled,
  className,
  closeTask,
  initialTaskData,
  isEditingTask = false,
  onTaskSubmit
}: TaskFormProps) {
  const [task, setTask] = useState<TaskParams | null>(null)
  const [disabled, setDisabled] = useState(false)
  const [flownamesString, setFlowNamesString] = useState<string | null>(null) // copy value from FlowNameSelector
  const navigate = useNavigate()
  const { uuid } = useParams<{ uuid: string }>()
  const { collapsed, toggleCollapsed } = useSidebarContext()
  const workspaceContext = useWorkspaceContext()
  const dispatch = useDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const isPlugin = useCompactViewport()
  const isInPlugin = bridge.isInPlugin()

  // Initialize isTryExample from query params or prop
  const [isTryExample, setIsTryExample] = useState<boolean>(() => {
    const paramValue = searchParams.get('withExamplePrefilled')
    return paramValue === 'true' || withExamplePrefilled === true
  })

  // Combined value: true if either prop or local state is true
  // This ensures forms are disabled and show example data when checkbox is checked
  const isExampleMode = isTryExample || withExamplePrefilled === true

  // Remember the in-progress form across soft reloads, but only for fresh task
  // creation (not when editing an existing task or previewing an example).
  const { draft, saveDraft, clearDraft } = useTaskFormDraft(
    command as CommandType,
    !isEditingTask && !isExampleMode
  )

  // Update query params when isTryExample changes
  useEffect(() => {
    setSearchParams(
      prev => {
        const newParams = new URLSearchParams(prev)
        newParams.set('withExamplePrefilled', isTryExample ? 'true' : 'false')
        return newParams
      },
      { replace: true } // Use replace to avoid adding to browser history
    )
  }, [isTryExample, setSearchParams])

  const computeInitialOption = useCallback((): ProjectSelectionOption => {
    // Respect existing task metadata if present
    if (initialTaskData?.request?.metadata) {
      const metadata = initialTaskData.request.metadata
      let metadataOption: ProjectSelectionOption | null = null

      if (metadata.multiRepoCodeContext && metadata.multiRepoCodeContext.length > 0) {
        const hasS3Connection = metadata.multiRepoCodeContext.some(
          (context: { connType?: string }) => context.connType === 'S3'
        )
        if (hasS3Connection) {
          metadataOption = 'upload'
        } else {
          const allHaveConnType = metadata.multiRepoCodeContext.every(
            (context: { connType?: string }) => context.connType && context.connType !== 'S3'
          )
          if (allHaveConnType) metadataOption = 'codeContext'
        }
      }

      if (
        !metadataOption &&
        metadata.codeContext &&
        (metadata.codeContext.orgName ||
          metadata.codeContext.repoName ||
          metadata.codeContext.connType)
      ) {
        metadataOption = metadata.codeContext.connType === 'S3' ? 'upload' : 'codeContext'
      }

      if (
        !metadataOption &&
        (!metadata.codeContext || Object.keys(metadata.codeContext).length === 0) &&
        (!metadata.multiRepoCodeContext || metadata.multiRepoCodeContext.length === 0)
      ) {
        metadataOption = 'none'
      }

      if (
        !metadataOption &&
        metadata.codeContext &&
        (metadata.codeContext.orgName ||
          metadata.codeContext.repoName ||
          metadata.codeContext.branchName)
      ) {
        metadataOption = 'codeContext'
      }

      return metadataOption || 'none'
    }

    // If IDE upload is provided via URL query param, start in upload to avoid UI flicker
    if (!isExampleMode) {
      const ideS3TarFilePath = searchParams.get('s3_tar_file_path')
      if (ideS3TarFilePath) {
        return 'upload'
      }
    }

    // Derive from command group via shared logic
    const cmd = command as CommandType
    const cmdGroup = getCommandGroup(cmd)
    const initState = {
      [CodeContextGroup.DATAWEAVE]: 'none',
      [CodeContextGroup.INTEGRATION]: 'none',
      [CodeContextGroup.SAMPLE_DATA]: 'none',
      [CodeContextGroup.CODE_ENHANCER]: 'upload'
    } as Record<CodeContextGroup, ProjectSelectionOption>
    const initialOption = initState[cmdGroup as CodeContextGroup]
    const normalizedOption = shouldUpdateOption(cmd, initialOption, cmdGroup)
    return normalizedOption || initialOption
  }, [initialTaskData, command, isExampleMode, searchParams])

  const group = getCommandGroup(command as CommandType)
  const [option, setOption] = useState<ProjectSelectionOption>(
    () => draft?.option ?? computeInitialOption()
  )
  const [initialized, setInitialized] = useState<boolean>(false)
  const { repositories, setRepositories, s3UploadPaths, setS3UploadPaths } = useCodeContext()

  // Restore the persisted repository selection once on mount (soft-reload recovery).
  useEffect(() => {
    if (draft?.repositories?.length) {
      setRepositories(draft.repositories)
    }
    // Intentionally run once; `draft` is loaded a single time by useTaskFormDraft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Persist the draft as the user edits, so a soft reload restores their place.
  useEffect(() => {
    saveDraft({ option })
  }, [option, saveDraft])
  useEffect(() => {
    saveDraft({ repositories })
  }, [repositories, saveDraft])
  useEffect(() => {
    const description = task?.request?.command?.commandString
    if (description !== undefined) {
      saveDraft({ description })
    }
  }, [task, saveDraft])

  // Extract initial repositories from initialTaskData (for editing mode)
  const initialRepositories = useMemo(() => {
    if (!initialTaskData?.request?.metadata) return []
    const contexts = initialTaskData.request.metadata.multiRepoCodeContext || []
    return contexts
      .filter(ctx => ctx.connType === 'S3')
      .map(ctx => ({
        s3Path: ctx.s3TarFilePath || ''
      }))
  }, [initialTaskData])

  // Memoized callback for MuleProjectUpload to prevent infinite renders
  const handleUploadComplete = useCallback(
    (paths: string[]) => {
      const filtered = paths.filter(Boolean)
      setS3UploadPaths(filtered)
    },
    [setS3UploadPaths]
  )

  /**
   * ============================================================================
   * INITIALIZATION EFFECTS (run once on mount or when key dependencies change)
   * ============================================================================
   * 1. Initialize option based on command group and sync with initialTaskData
   * 2. Initialize task from initialTaskData (editing mode)
   * 3. Initialize runtime settings (priority: edit > example > index > org)
   * 4. Initialize oldRuntimeSettings (for MULE_VERSION_UPGRADE)
   * 5. Register global event listener for task submission
   */

  // 1. Initialize option based on command group and sync with initialTaskData
  useEffect(() => {
    let finalOption: ProjectSelectionOption
    if (initialized) return

    // Respect a restored draft: `option` was already seeded from sessionStorage,
    // so don't overwrite it with the computed default on mount.
    if (draft?.option && !initialTaskData) {
      setInitialized(true)
      return
    }

    if (initialTaskData) {
      // Editing mode: sync option with existing task metadata
      const codeContext = initialTaskData.request.metadata?.codeContext
      const multiRepoCodeContext = initialTaskData.request.metadata?.multiRepoCodeContext || []
      const context = codeContext || multiRepoCodeContext[0]
      if (context?.connType === 'S3') {
        finalOption = 'upload'
      } else if (context?.connType && context.connType !== 'S3') {
        finalOption = 'codeContext'
      } else {
        finalOption = 'none'
      }
    } else {
      // Not editing: get initial option from group
      const initState = {
        [CodeContextGroup.DATAWEAVE]: 'none',
        [CodeContextGroup.INTEGRATION]: 'none',
        [CodeContextGroup.SAMPLE_DATA]: 'none',
        [CodeContextGroup.CODE_ENHANCER]: 'upload'
      } as Record<CodeContextGroup, ProjectSelectionOption>
      const initialOption = initState[group as CodeContextGroup]

      // Then normalize based on command requirements
      const normalizedOption = shouldUpdateOption(command as CommandType, initialOption, group)
      finalOption = normalizedOption || initialOption
    }

    setOption(finalOption)
    setInitialized(true)
  }, [command, group, initialTaskData, initialized, draft])

  // Helper function to handle option changes and command conversion
  // setS3UploadPaths is stable from hook; declare after hook usage
  const handleOptionChange = useCallback((requestedOption: ProjectSelectionOption) => {
    setOption(requestedOption)
  }, [])

  useEffect(() => {
    const newCommand = resolveCommandFromOption(group, option)
    if (newCommand && newCommand !== command) {
      globalEventEmitter.emit(AppEvents.OpenCommand, {
        command: AgentCommands[newCommand as UsedCommandType],
        withExamplePrefilled: withExamplePrefilled || false
      })
    }
  }, [group, option, withExamplePrefilled, command])

  // Auto-switch to 'upload' mode when IDE integration provides s3 path via URL query param
  // This ensures repo metadata is fetched for IDE uploads just like manual uploads
  useEffect(() => {
    // Skip if we're editing a task (option already set from initialTaskData)
    if (isEditingTask) {
      return
    }

    // Skip if we're in example mode (option should remain as configured)
    if (isExampleMode) {
      return
    }

    // If we have s3 uploads from IDE but option is not 'upload', auto-switch to trigger metadata fetch
    const ideS3TarFilePath = searchParams.get('s3_tar_file_path')
    if (ideS3TarFilePath && option !== 'upload') {
      setOption('upload')
    }
  }, [option, isEditingTask, isExampleMode, searchParams])

  // 3. Initialize form with existing task data if provided (editing mode)
  useEffect(() => {
    if (initialTaskData && !task) {
      setTask(initialTaskData)
    }
  }, [initialTaskData, task])

  const setCommandString = (commandString: string) => {
    setTask((task: TaskParams | null) => {
      const command = Command.fromPartial({
        ...task?.request.command,
        commandString
      })
      return {
        ...task,
        request: AgentRequest.fromPartial({
          ...task?.request,
          command
        })
      }
    })
  }

  // S3 uploads are managed via useCodeContext.s3UploadPaths
  // Note: updateConversation hook removed - metadata is now handled by backend

  // selection is represented by repositories state
  const { indexes } = useIndexesContext()
  const traceWithUserInfo = useTracer()

  // Parse repository name - use the context repository directly
  const primaryRepo = repositories[0]
  const repository = primaryRepo?.repository || ''
  const conn_type = primaryRepo?.conn_type || ''
  const branch = primaryRepo?.branch || ''
  const [parsedOrgName, parsedRepoName] = parseRepositoryName(repository)

  // Find matching index for the selected repository
  const matchingIndex = useMemo(() => {
    if (!parsedOrgName || !parsedRepoName || !branch) return null
    return indexes.find(
      index =>
        index.org_name === parsedOrgName &&
        index.repo_name === parsedRepoName &&
        index.branch_name === branch
    )
  }, [parsedOrgName, parsedRepoName, branch, indexes])

  // Determine which agents need repo metadata (VM args, flow names, or both)
  const needsRepoMetadata = useMemo(() => {
    const needs = [
      // Agents needing VM args
      CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION_REPO_AWARE,
      CommandType.DATAWEAVE_TO_DATA_CREATION_REPO_AWARE,
      CommandType.GENERATE_SIMPLE_CODE_PR,
      CommandType.MULE_VERSION_UPGRADE,
      // Agents needing flow names
      CommandType.GENERATE_DOCUMENTATION,
      CommandType.GENERATE_IMAGE,
      // Agents needing both (e.g., MUnit)
      CommandType.MUNIT_TEST_CREATION
    ].includes(command!)
    return needs
  }, [command])

  // Unified API call to fetch both VM args and flow names in a single request
  // This prevents concurrent request conflicts when agents need both
  const repoMetadataParams = {
    connType: conn_type || '',
    orgName: parsedOrgName,
    repoName: parsedRepoName,
    branchName: branch || '',
    commitId: '',
    s3TarFilePath: s3UploadPaths.join(','),
    currentOption: option as 'upload' | 'codeContext',
    enabled: needsRepoMetadata && (option === 'codeContext' || option === 'upload'),
    commandType: command
  }

  const {
    flowNames,
    suggestedVmArgs,
    suggestedJavaVersion,
    suggestedMvnVersion,
    suggestedMinMuleRuntimeVersion,
    detectedPlatform,
    isFetching: isFetchingRepoMetadata
  } = useRepoMetadata(repoMetadataParams)

  // Enhanced loading state: show loading when we have required fields and are fetching
  const hasRequiredFields = Boolean(
    (option === 'codeContext' && conn_type && parsedOrgName && parsedRepoName && branch) ||
      (option === 'upload' && s3UploadPaths.length > 0)
  )
  const shouldShowLoading = Boolean(hasRequiredFields && isFetchingRepoMetadata)

  const [taskRuntimeSettings, setTaskRuntimeSettings] = useState<
    Record<string, string | undefined>
  >({
    javaVersion: '',
    mavenVersion: '',
    muleRuntimeVersion: '',
    settingsXmlRawTxt: '',
    settingsSecurityXmlRawTxt: '',
    vm_args: ''
  })

  // Current runtime settings for MULE_VERSION_UPGRADE
  const [oldRuntimeSettings, setOldRuntimeSettings] = useState<Record<string, string | undefined>>({
    javaVersion: '',
    mavenVersion: '',
    muleRuntimeVersion: ''
  })

  const needFlowNameInput = [
    CommandType.MUNIT_TEST_CREATION,
    CommandType.GENERATE_DOCUMENTATION,
    CommandType.GENERATE_IMAGE
  ].includes(command!)
  const [isMultiSelect, setIsMultiSelect] = useState<boolean | undefined>(undefined)
  const [isIntegrationRepoModalOpen, setIntegrationRepoModalOpen] = useState(false)
  const [uploadSource, setUploadSource] = useState<UploadSourceOption>('computer')
  const pluginWs = usePluginWorkspaceSafe()
  const [isWorkspaceUploading, setIsWorkspaceUploading] = useState(false)
  const [uploadedIdeProjects, setUploadedIdeProjects] = useState<Map<string, string>>(new Map())
  const [selectedIdePaths, setSelectedIdePaths] = useState<Set<string>>(new Set())

  // State for "Run Verification" checkbox for specific commands
  const [runVerification, setRunVerification] = useState<boolean>(true)

  // Redux state and dispatch for integration test flow
  const currentFlowStep = useSelector(selectCurrentStep)
  const isInFlow = useSelector(selectIsInFlow)

  // Redirect to landing page if integration test command is accessed without being in flow
  // Note: 'selectRepository' is the pre-flow modal step, so we should NOT redirect during that
  useEffect(() => {
    if (command === CommandType.INTEGRATION_TEST && currentFlowStep === 'idle' && !isEditingTask) {
      // User accessed integration-test command directly without going through proper flow
      // Expand sidebar before redirecting to landing page
      if (collapsed) {
        toggleCollapsed()
      }
      // Redirect to landing page to start the flow properly
      if (workspaceContext.currentWorkspace?.uuid) {
        navigate(`/w/${workspaceContext.currentWorkspace.uuid}`)
      }
    }
  }, [
    command,
    currentFlowStep,
    isEditingTask,
    uuid,
    workspaceContext.currentWorkspace?.uuid,
    navigate,
    collapsed,
    toggleCollapsed
  ])

  // Cleanup: Reset integration test flow when component unmounts or command changes
  useEffect(() => {
    return () => {
      if (isInFlow) {
        dispatch(resetFlow())
      }
    }
  }, [dispatch, isInFlow]) // Only run on unmount

  // Reset flow when switching away from integration test command
  useEffect(() => {
    if (command !== CommandType.INTEGRATION_TEST && isInFlow) {
      dispatch(resetFlow())
    }
  }, [command, isInFlow, dispatch])

  const multiRepoMode = isMultiRepoRequired(command)

  const deselectIdeProject = (projectPath: string) => {
    const s3Path = uploadedIdeProjects.get(projectPath)
    if (s3Path) {
      setUploadedIdeProjects(prev => {
        const next = new Map(prev)
        next.delete(projectPath)
        return next
      })
      setS3UploadPaths(prev => (prev || []).filter(p => p !== s3Path))
    }
    setSelectedIdePaths(prev => {
      const next = new Set(prev)
      next.delete(projectPath)
      return next
    })
  }

  const clearAllUploadedIdeProjects = () => {
    uploadedIdeProjects.forEach((_s3Path, projectPath) => {
      deselectIdeProject(projectPath)
    })
  }

  const handleIdeWorkspaceUpload = async () => {
    if (!isInPlugin || isWorkspaceUploading) return
    const projects = (pluginWs?.projects ?? []).filter(p => selectedIdePaths.has(p.path))
    if (projects.length === 0) return

    if (!multiRepoMode) {
      clearAllUploadedIdeProjects()
    }

    const workspaceUuid = workspaceContext.currentWorkspace?.uuid
    setIsWorkspaceUploading(true)
    try {
      for (const project of projects) {
        if (uploadedIdeProjects.has(project.path)) continue
        const uploadResult = await projectService.uploadProject(
          project.path,
          project.name || 'workspace',
          workspaceUuid
        )

        if (!uploadResult.success || !uploadResult.s3Path) {
          BetterToast.error(uploadResult.error || `Failed to upload ${project.name}`)
          continue
        }

        const nextPaths = new Set([...(s3UploadPaths || []), uploadResult.s3Path])
        setS3UploadPaths(Array.from(nextPaths))
        setUploadedIdeProjects(prev => new Map(prev).set(project.path, uploadResult.s3Path!))
      }
    } catch (error) {
      BetterToast.error(
        `Failed to upload IDE workspace: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    } finally {
      setIsWorkspaceUploading(false)
    }
  }

  const toggleIdeProject = (path: string) => {
    if (!multiRepoMode) {
      clearAllUploadedIdeProjects()
    }
    setSelectedIdePaths(prev => {
      if (multiRepoMode) {
        const next = new Set(prev)
        if (next.has(path)) next.delete(path)
        else next.add(path)
        return next
      }
      return prev.has(path) ? new Set<string>() : new Set([path])
    })
  }

  const renderUploadInput = () => {
    const isUploadDisabled = isExampleMode || (isEditingTask && initialUploads.length > 0)
    const showIdeUpload = isInPlugin && uploadSource === 'ide' && !isUploadDisabled

    return (
      <>
        {isInPlugin ? (
          <div className='mb-3 inline-flex items-center rounded-xl border border-default-200 bg-white p-1'>
            <button
              type='button'
              onClick={() => setUploadSource('computer')}
              className={cm(
                'h-8 px-3 rounded-lg text-sm',
                uploadSource === 'computer'
                  ? 'bg-default-100 text-default-800 font-medium'
                  : 'text-default-600'
              )}
              disabled={isUploadDisabled}
            >
              Computer
            </button>
            <button
              type='button'
              onClick={() => {
                setUploadSource('ide')
              }}
              className={cm(
                'h-8 px-3 rounded-lg text-sm',
                uploadSource === 'ide'
                  ? 'bg-default-100 text-default-800 font-medium'
                  : 'text-default-600'
              )}
              disabled={isUploadDisabled}
            >
              IDE Workspace
            </button>
          </div>
        ) : null}

        {showIdeUpload ? (
          <div className='border border-default-200 rounded-xl p-3'>
            {!pluginWs?.projects || pluginWs.projects.length === 0 ? (
              <div className='text-sm text-default-500'>No projects found in IDE workspace</div>
            ) : isWorkspaceUploading ? (
              <div className='flex items-center gap-2'>
                <svg
                  className='w-4 h-4 animate-spin text-default-400'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  />
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                  />
                </svg>
                <span className='text-sm text-default-500'>Uploading selected projects...</span>
              </div>
            ) : (
              <div className='flex flex-col gap-1'>
                {pluginWs.projects.map(project => {
                  const isUploaded = uploadedIdeProjects.has(project.path)
                  const isSelected = selectedIdePaths.has(project.path)
                  return (
                    <button
                      key={project.path}
                      type='button'
                      onClick={() => {
                        if (isUploaded) {
                          deselectIdeProject(project.path)
                        } else {
                          toggleIdeProject(project.path)
                        }
                      }}
                      disabled={isWorkspaceUploading}
                      className={cm(
                        'flex items-center gap-2.5 w-full rounded-lg px-2 py-2 text-left transition-colors',
                        isUploaded
                          ? 'bg-primary-50 hover:bg-default-100'
                          : isSelected
                            ? 'bg-primary-50 hover:bg-primary-100'
                            : 'hover:bg-default-100'
                      )}
                    >
                      {isUploaded ? (
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          viewBox='0 0 20 20'
                          fill='currentColor'
                          className='w-4 h-4 flex-shrink-0 text-success'
                        >
                          <path
                            fillRule='evenodd'
                            d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z'
                            clipRule='evenodd'
                          />
                        </svg>
                      ) : multiRepoMode ? (
                        <div
                          className={cm(
                            'w-4 h-4 flex-shrink-0 rounded border-2 flex items-center justify-center transition-colors',
                            isSelected ? 'bg-primary border-primary' : 'border-default-300'
                          )}
                        >
                          {isSelected && (
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              viewBox='0 0 16 16'
                              fill='white'
                              className='w-3 h-3'
                            >
                              <path
                                fillRule='evenodd'
                                d='M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z'
                                clipRule='evenodd'
                              />
                            </svg>
                          )}
                        </div>
                      ) : (
                        <div
                          className={cm(
                            'w-4 h-4 flex-shrink-0 rounded-full border-2 flex items-center justify-center transition-colors',
                            isSelected ? 'border-primary' : 'border-default-300'
                          )}
                        >
                          {isSelected && <div className='w-2 h-2 rounded-full bg-primary' />}
                        </div>
                      )}
                      <div>
                        <div className='text-sm font-medium text-default-800'>{project.name}</div>
                        {isUploaded && (
                          <div className='text-xs text-default-400'>Uploaded from IDE</div>
                        )}
                      </div>
                    </button>
                  )
                })}
                {selectedIdePaths.size > 0 &&
                  !Array.from(selectedIdePaths).every(p => uploadedIdeProjects.has(p)) && (
                    <button
                      type='button'
                      onClick={handleIdeWorkspaceUpload}
                      className='mt-2 w-full h-9 rounded-lg border border-default-200 text-sm font-medium text-default-700 hover:bg-default-100 transition-colors'
                    >
                      Upload {selectedIdePaths.size - uploadedIdeProjects.size} Project
                      {selectedIdePaths.size - uploadedIdeProjects.size !== 1 ? 's' : ''}
                    </button>
                  )}
              </div>
            )}
          </div>
        ) : (
          <MuleProjectUpload
            data-testid='mule-upload'
            onUploadComplete={handleUploadComplete}
            withExamplePrefilled={isExampleMode}
            initialUploads={initialUploads}
            multiRepo={isMultiRepoRequired(command)}
            disabled={isUploadDisabled}
          />
        )}
      </>
    )
  }

  // Memoize initialUploads to prevent unnecessary re-renders
  // Priority: 1. Editing mode (from task metadata), 2. Plugin upload (from useCodeContext s3UploadPaths)
  const initialUploads = useMemo(() => {
    const repos: { name: string; s3Path: string }[] = []

    // Priority 1: Editing mode - use existing task metadata
    if (isEditingTask && initialTaskData?.request?.metadata) {
      const metadata = initialTaskData.request.metadata

      // Handle multiRepoCodeContext (multiple repositories)
      if (metadata.multiRepoCodeContext && metadata.multiRepoCodeContext.length > 0) {
        const multiRepoContext = metadata.multiRepoCodeContext
        const repositoryNames = initialTaskData.request.command?.commandString?.split(',') || []

        return multiRepoContext
          .map((repo, index) => ({
            name: repositoryNames[index]?.trim() || repo.repoName || `Repository ${index + 1}`,
            s3Path: repo.s3TarFilePath || ''
          }))
          .filter(repo => repo.s3Path)
      }
      // Handle single codeContext (single repository)
      else if (metadata.codeContext && metadata.codeContext.s3TarFilePath) {
        const codeContext = metadata.codeContext
        return [
          {
            name: codeContext.repoName || 'Repository',
            s3Path: codeContext.s3TarFilePath
          }
        ]
      }
    }

    // Priority 2: Plugin upload - use s3UploadPaths from useCodeContext (already initialized from URL param)
    if (!isEditingTask && !isExampleMode && s3UploadPaths.length > 0) {
      return s3UploadPaths.map(s3Path => {
        // Extract display name from s3 path
        const fullFileName = s3Path.split('/').pop() || 'IDE Upload'
        let displayName = fullFileName

        // Parse the filename format: {uuid}-{timestamp}___{originalName}.tar.gz
        if (fullFileName.includes('___')) {
          const parts = fullFileName.split('___')
          if (parts.length > 1) {
            displayName = parts[1].replace('.tar.gz', '')
          }
        }

        return {
          name: displayName,
          s3Path
        }
      })
    }

    return repos
  }, [isEditingTask, initialTaskData, isExampleMode, s3UploadPaths])

  // 4. Initialize runtime settings (consolidated effect)
  // Priority: Editing mode > Example prefilled > MULE_VERSION_UPGRADE hardcoded > Suggested from repo > Matching index > Org defaults
  // IMPORTANT: For MULE_VERSION_UPGRADE, target runtime is hardcoded to Java 17, Maven 3.9, Mule 4.9
  // For other commands, suggested values from repo-metadata have higher priority than matchingIndex
  useEffect(() => {
    const orgRuntimeSetting = workspaceContext.currentWorkspace?.org_runtime_setting

    // Priority 1: Editing mode - use existing task runtime settings
    if (isEditingTask && initialTaskData?.request?.runtimeSettings) {
      const runtimeSettings = initialTaskData.request.runtimeSettings
      setTaskRuntimeSettings(prev => ({
        ...prev,
        javaVersion: runtimeSettings.javaVersion || prev.javaVersion,
        mavenVersion: runtimeSettings.mavenVersion || prev.mavenVersion,
        muleRuntimeVersion: runtimeSettings.muleRuntimeVersion || prev.muleRuntimeVersion,
        settingsXmlRawTxt: runtimeSettings.settingsXmlRawTxt || prev.settingsXmlRawTxt,
        settingsSecurityXmlRawTxt:
          runtimeSettings.settingsSecurityXmlRawTxt || prev.settingsSecurityXmlRawTxt
      }))
      return
    }

    // Priority 2: Example mode - use hardcoded versions
    if (isExampleMode) {
      setTaskRuntimeSettings({
        javaVersion: JavaVersion['8_0'],
        mavenVersion: MavenVersion['3_8'],
        muleRuntimeVersion: MuleRuntimeVersion['4_8']
      })
      return
    }

    // Priority 3: MULE_VERSION_UPGRADE - hardcode target runtime to Java 17, Maven 3.9, Mule 4.9
    if (command === CommandType.MULE_VERSION_UPGRADE) {
      // Get base XML settings from matching index or org
      const settingsXmlRawTxt =
        matchingIndex?.settings_xml_raw_txt || orgRuntimeSetting?.settings_xml_raw_txt || ''
      const settingsSecurityXmlRawTxt =
        matchingIndex?.settings_security_xml_raw_txt ||
        orgRuntimeSetting?.settings_security_xml_raw_txt ||
        ''

      setTaskRuntimeSettings({
        javaVersion: JavaVersion['17_0'],
        mavenVersion: MavenVersion['3_9'],
        muleRuntimeVersion: MuleRuntimeVersion['4_9'],
        settingsXmlRawTxt,
        settingsSecurityXmlRawTxt,
        vm_args: resolveVmArgs({ isMuleVersionUpgrade: true, suggestedVmArgs })
      })
      return
    }

    // Priority 4: Suggested from repo metadata - use detected versions from repository (for non-upgrade commands)
    // MOVED UP: This should have priority over matchingIndex since it reflects actual repo state
    if (
      suggestedJavaVersion ||
      suggestedMvnVersion ||
      suggestedMinMuleRuntimeVersion ||
      suggestedVmArgs
    ) {
      // Get base XML settings from matching index or org
      const settingsXmlRawTxt =
        matchingIndex?.settings_xml_raw_txt || orgRuntimeSetting?.settings_xml_raw_txt || ''
      const settingsSecurityXmlRawTxt =
        matchingIndex?.settings_security_xml_raw_txt ||
        orgRuntimeSetting?.settings_security_xml_raw_txt ||
        ''

      setTaskRuntimeSettings({
        javaVersion: suggestedJavaVersion,
        mavenVersion: suggestedMvnVersion,
        muleRuntimeVersion: suggestedMinMuleRuntimeVersion,
        settingsXmlRawTxt,
        settingsSecurityXmlRawTxt,
        vm_args: resolveVmArgs({ suggestedVmArgs, matchingIndexVmArgs: matchingIndex?.vm_args })
      })
      return
    }

    // Priority 5: Matching index - use repo-level settings (only if no suggested values)
    if (matchingIndex) {
      setTaskRuntimeSettings({
        javaVersion: matchingIndex.java_version || '',
        mavenVersion: matchingIndex.maven_version || '',
        muleRuntimeVersion: matchingIndex.mule_runtime_version || '',
        settingsXmlRawTxt:
          matchingIndex.settings_xml_raw_txt || orgRuntimeSetting?.settings_xml_raw_txt || '',
        settingsSecurityXmlRawTxt:
          matchingIndex.settings_security_xml_raw_txt ||
          orgRuntimeSetting?.settings_security_xml_raw_txt ||
          '',
        vm_args: matchingIndex.vm_args || ''
      })
      return
    }

    // Priority 6: Org defaults - use org-level XML settings only
    setTaskRuntimeSettings({
      javaVersion: '',
      mavenVersion: '',
      muleRuntimeVersion: '',
      settingsXmlRawTxt: orgRuntimeSetting?.settings_xml_raw_txt || '',
      settingsSecurityXmlRawTxt: orgRuntimeSetting?.settings_security_xml_raw_txt || '',
      vm_args: ''
    })
  }, [
    isEditingTask,
    initialTaskData,
    withExamplePrefilled,
    isExampleMode,
    matchingIndex,
    workspaceContext,
    suggestedJavaVersion,
    suggestedMvnVersion,
    suggestedMinMuleRuntimeVersion,
    suggestedVmArgs,
    command
  ])

  // 5. Initialize oldRuntimeSettings (for MULE_VERSION_UPGRADE)
  useEffect(() => {
    // Priority 1: Editing mode - use existing old runtime settings
    if (isEditingTask && initialTaskData?.request?.oldRuntimeSettings) {
      const initialTaskSettings = initialTaskData.request.oldRuntimeSettings
      setOldRuntimeSettings({
        javaVersion: initialTaskSettings.javaVersion || '',
        mavenVersion: initialTaskSettings.mavenVersion || '',
        muleRuntimeVersion: initialTaskSettings.muleRuntimeVersion || ''
      })
      return
    }

    // Priority 2: For MULE_VERSION_UPGRADE, use suggested versions from repo (current project versions)
    if (
      command === CommandType.MULE_VERSION_UPGRADE &&
      (suggestedJavaVersion || suggestedMvnVersion || suggestedMinMuleRuntimeVersion)
    ) {
      setOldRuntimeSettings(prev => ({
        ...prev,
        javaVersion: prev.javaVersion || suggestedJavaVersion || '',
        mavenVersion: prev.mavenVersion || suggestedMvnVersion || '',
        muleRuntimeVersion: prev.muleRuntimeVersion || suggestedMinMuleRuntimeVersion || ''
      }))
    }
  }, [
    isEditingTask,
    initialTaskData,
    command,
    suggestedJavaVersion,
    suggestedMvnVersion,
    suggestedMinMuleRuntimeVersion
  ])

  const repositoryCoderSourcePlatform = useMemo(() => {
    if (command !== CommandType.GENERATE_SIMPLE_CODE_PR) {
      return undefined
    }
    return normalizeRepositoryCoderPlatform(
      task?.request?.platform ?? initialTaskData?.request?.platform
    )
  }, [command, initialTaskData?.request?.platform, task?.request?.platform])

  const isRepositoryCoderMuleSourcePlatform =
    repositoryCoderSourcePlatform === IntegrationPlatform.MULESOFT
  const isRepositoryCoderJvmSourcePlatform = isRepositoryCoderJvmPlatform(
    repositoryCoderSourcePlatform
  )
  const isRepositoryCoderGenericNonJvmSourcePlatform = Boolean(
    command === CommandType.GENERATE_SIMPLE_CODE_PR &&
      repositoryCoderSourcePlatform &&
      !isRepositoryCoderMuleSourcePlatform &&
      !isRepositoryCoderJvmSourcePlatform
  )
  const showMuleRuntimeSelection = !(
    command === CommandType.GENERATE_SIMPLE_CODE_PR && isRepositoryCoderJvmSourcePlatform
  )

  const isHideRuntimeSelect = useCallback(() => {
    if (option === 'none') {
      return true
    }

    if (isRepositoryCoderGenericNonJvmSourcePlatform) {
      return true
    }

    // Hide MuleSoft runtime settings for non-MuleSoft platforms
    if (
      command !== CommandType.GENERATE_SIMPLE_CODE_PR &&
      (detectedPlatform === IntegrationPlatform.INFORMATICA ||
        detectedPlatform === IntegrationPlatform.BOOMI)
    ) {
      return true
    }

    switch (command) {
      case CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION:
      case CommandType.GENERATE_DATAWEAVE:
      case CommandType.DATAWEAVE_TO_DATA_CREATION:
      case CommandType.GENERATE_IMAGE:
      case CommandType.GENERATE_MULTI_REPO_FLOW_DIAGRAM:
      case CommandType.GENERATE_MAPPING_TABLE:
      case CommandType.QUESTION_ANSWERING:
      case CommandType.MULTI_REPO_QA:
      case CommandType.AUTODESK_MULE_TO_NODE_JS_MIGRATION:
      case CommandType.MIGRATION_DOCUMENT:
      case CommandType.BILLABLE_FLOW_DOCUMENTATION:
      case CommandType.GENERATE_DOCUMENTATION: // Documentation Generator
      case CommandType.MULE_3_TO_MULE_4_MIGRATION:
      case CommandType.MIGRATION_ACCELERATOR:
      case CommandType.MULE_REFACTORING:
      case CommandType.SPRING_INTEGRATION_REFACTORING:
      case CommandType.AZURE_DEPLOY_TEST: // Azure Logic Apps, not JVM/Mule — runtime versions N/A
        return true
      default:
        return false
    }
  }, [option, command, detectedPlatform, isRepositoryCoderGenericNonJvmSourcePlatform])

  const getDesc = () => {
    if (command != null) {
      return AgentCommands[command] ? (
        <div className='flex items-start title-sm-normal select-none'>
          {AgentCommands[command].desc}
        </div>
      ) : null
    }
    return null
  }

  const getTitle = () => {
    if (command != null) {
      return AgentCommands[command] ? (
        <div className='flex items-start headline-md-bold select-none'>
          {AgentCommands[command].label}
        </div>
      ) : null
    }
    return null
  }

  // Check if code context requirements are satisfied using child component states
  const codeContextNotSatisfied = useMemo(() => {
    if (option === 'upload') {
      // For upload option, check if we have upload state or S3 paths
      return s3UploadPaths.length === 0
    } else if (option === 'codeContext') {
      let satisfied = repositories.length > 0
      for (const repository of repositories) {
        if (
          !(
            Boolean(repository.repository) &&
            Boolean(repository.branch) &&
            Boolean(repository.conn_type)
          )
        ) {
          satisfied = false
          break
        }
      }
      return !satisfied
    }
    return false
  }, [option, s3UploadPaths, repositories])

  // Check if runtime settings are satisfied when required
  const runtimeSettingsNotSatisfied = useMemo(() => {
    // If runtime settings are hidden, they're not required
    if (isHideRuntimeSelect()) {
      return false
    }

    // Runtime settings are required: all three versions must be selected
    const targetRuntimeNotSatisfied =
      !taskRuntimeSettings.javaVersion ||
      !taskRuntimeSettings.mavenVersion ||
      (showMuleRuntimeSelection && !taskRuntimeSettings.muleRuntimeVersion)

    // For MULE_VERSION_UPGRADE, also validate current runtime settings
    if (command === CommandType.MULE_VERSION_UPGRADE) {
      const currentRuntimeNotSatisfied =
        !oldRuntimeSettings.javaVersion ||
        !oldRuntimeSettings.mavenVersion ||
        !oldRuntimeSettings.muleRuntimeVersion
      return targetRuntimeNotSatisfied || currentRuntimeNotSatisfied
    }

    return targetRuntimeNotSatisfied
  }, [
    taskRuntimeSettings,
    oldRuntimeSettings,
    command,
    isHideRuntimeSelect,
    showMuleRuntimeSelection
  ])

  const fieldsNotSatisfied = areRequiredFieldsNotSatisfied(command, task)

  // Build a user-facing reason for why submit is blocked, in priority order.
  // Used both to disable the submit button and to drive the toast in onSubmit.
  const formIncompleteReason = useMemo<string | undefined>(() => {
    if (codeContextNotSatisfied) {
      return option === 'upload'
        ? 'Please attach the code files you want to use as context.'
        : 'Please select at least one repository and branch before submitting.'
    }
    if (!isExampleMode && fieldsNotSatisfied) {
      return getRequiredFieldsErrorMessage(command, task)
    }
    if (runtimeSettingsNotSatisfied) {
      return 'Please choose Java, Maven, and Mule runtime versions before submitting.'
    }
    if (task?.submitBlockedReason) {
      return task.submitBlockedReason
    }
    return undefined
  }, [
    codeContextNotSatisfied,
    option,
    isExampleMode,
    fieldsNotSatisfied,
    runtimeSettingsNotSatisfied,
    command,
    task
  ])

  const onSubmit = useCallback(async () => {
    if (formIncompleteReason) {
      BetterToast.error(formIncompleteReason)
      return
    }

    if (!task) {
      BetterToast.error('Please complete the form before submitting.')
      return
    }

    if (command === CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION) {
      for (const inputOutputExample of task.request.sourceOutputExamples ?? []) {
        if (
          inputOutputExample.sourceData == null ||
          inputOutputExample.outputData == null ||
          inputOutputExample.sourceData?.length === 0 ||
          inputOutputExample.outputData?.length === 0
        ) {
          BetterToast.error('Please fill in all fields')
          return
        }
      }
    }
    // Handle upload option using s3UploadPaths from context
    if (option === 'upload' && s3UploadPaths.length > 0) {
      const isMultiRepo = isMultiRepoRequired(command)
      if (isMultiRepo) {
        const multiRepoContexts = s3UploadPaths.map(path =>
          CodeContext.fromPartial({
            connType: 'S3',
            s3TarFilePath: path
          })
        )

        task.request.metadata = Metadata.fromPartial({
          multiRepoCodeContext: multiRepoContexts
        })
      } else {
        task.request.metadata = Metadata.fromPartial({
          codeContext: CodeContext.fromPartial({
            connType: 'S3',
            s3TarFilePath: s3UploadPaths.join(',')
          })
        })
      }
    }
    if (option === 'codeContext') {
      const isMultiRepo = isRepoNotRequired(command)
      const codeContexts = repositories.map(r => {
        const [orgName, repoName] = parseRepositoryName(r.repository)
        return CodeContext.fromPartial({
          connType: r.conn_type,
          orgName,
          repoName,
          branchName: r.branch
        })
      })
      if (isMultiRepo) {
        task.request.metadata = Metadata.fromPartial({ multiRepoCodeContext: codeContexts })
      } else if (repositories[0]) {
        const [orgName, repoName] = parseRepositoryName(repositories[0].repository)
        task.request.metadata = Metadata.fromPartial({
          codeContext: CodeContext.fromPartial({
            connType: conn_type,
            orgName,
            repoName,
            branchName: branch
          })
        })
      }
    }

    setDisabled(true)

    if (isHideRuntimeSelect()) {
      if (option === 'none') {
        task.request.runtimeSettings = RuntimeSettings.fromPartial({
          javaVersion: JavaVersion['17_0'],
          mavenVersion: MavenVersion['3_9'],
          muleRuntimeVersion: isRepositoryCoderGenericNonJvmSourcePlatform
            ? ''
            : MuleRuntimeVersion['4_9'],
          settingsXmlRawTxt: '',
          settingsSecurityXmlRawTxt: ''
        })
      } else {
        task.request.runtimeSettings = RuntimeSettings.fromPartial({
          javaVersion: DefaultVersion.javaVersion,
          mavenVersion: DefaultVersion.mavenVersion,
          muleRuntimeVersion: isRepositoryCoderGenericNonJvmSourcePlatform
            ? ''
            : DefaultVersion.muleRuntimeVersion,
          settingsXmlRawTxt: '',
          settingsSecurityXmlRawTxt: ''
        })
      }
    } else {
      task.request.runtimeSettings = RuntimeSettings.fromPartial({
        javaVersion: taskRuntimeSettings.javaVersion,
        mavenVersion: taskRuntimeSettings.mavenVersion,
        muleRuntimeVersion: showMuleRuntimeSelection ? taskRuntimeSettings.muleRuntimeVersion : '',
        settingsXmlRawTxt: taskRuntimeSettings.settingsXmlRawTxt,
        settingsSecurityXmlRawTxt: taskRuntimeSettings.settingsSecurityXmlRawTxt
      })
    }

    if (command === CommandType.MULE_VERSION_UPGRADE) {
      task.request.oldRuntimeSettings = RuntimeSettings.fromPartial({
        javaVersion: oldRuntimeSettings.javaVersion,
        mavenVersion: oldRuntimeSettings.mavenVersion,
        muleRuntimeVersion: oldRuntimeSettings.muleRuntimeVersion
      })
    }

    // Pass detected platform to the backend for correct coder routing unless
    // the child form already supplied an explicit platform selection.
    const hasExplicitPlatform = Boolean(
      task.request.platform &&
        task.request.platform !== IntegrationPlatform.UNKNOW_INTEGRATION_PLATFORM &&
        task.request.platform !== IntegrationPlatform.UNRECOGNIZED
    )
    if (
      !hasExplicitPlatform &&
      detectedPlatform &&
      detectedPlatform !== IntegrationPlatform.UNRECOGNIZED
    ) {
      task.request.platform = detectedPlatform
    }

    traceWithUserInfo({
      event: isExampleMode ? 'try_builtin_example' : 'try_own_example',
      labels: {
        command: command?.toString() || ''
      }
    })

    if (command && task.request.command) {
      task.request.command.commandType = command

      // Set execution_mode and verification_mode for specific commands
      if (
        command === CommandType.GENERATE_SIMPLE_CODE_PR ||
        command === CommandType.MULE_VERSION_UPGRADE
      ) {
        // These commands default to agent mode
        task.request.command.executionMode = ExecutionMode.AGENT
        // Set verification mode based on checkbox
        task.request.command.verificationMode = runVerification
          ? VerificationMode.VERIFY
          : VerificationMode.FAST
      }
    } else {
      // Fallback: if command is undefined, don't submit
      BetterToast.error('Command type is missing. Cannot submit task.')
      setDisabled(false)
      return
    }

    let isSuccess = false

    try {
      // Always use the provided onTaskSubmit handler (from parent component)
      isSuccess = await onTaskSubmit(task)

      if (isSuccess) {
        // Note: Conversation metadata is now automatically updated by the backend
        // during task submission (see _update_conversation_metadata in task.py)
        // Parent component handles navigation and closing the form
        clearDraft()
      }
    } catch (error: unknown) {
      // Handle structured API error responses
      if (error && typeof error === 'object' && 'detail' in error) {
        const detail = (error as Record<string, unknown>).detail

        // Handle nested error structure with message
        if (detail && typeof detail === 'object' && 'message' in detail) {
          const errorMessage = (detail as Record<string, unknown>).message
          BetterToast.error(
            `Failed to submit task: ${typeof errorMessage === 'string' ? errorMessage : 'Unknown error'}`
          )
        } else if (typeof detail === 'string') {
          BetterToast.error(`Failed to submit task: ${detail}`)
        } else {
          BetterToast.error('Failed to submit task. Please try again.')
        }
      } else if (error instanceof Error) {
        BetterToast.error(`Failed to submit task: ${error.message}`)
      } else {
        BetterToast.error('Failed to submit task. Please try again.')
      }
    } finally {
      setDisabled(false)
    }
  }, [
    task,
    option,
    command,
    onTaskSubmit,
    taskRuntimeSettings,
    oldRuntimeSettings,
    traceWithUserInfo,
    isExampleMode,
    isHideRuntimeSelect,
    detectedPlatform,
    branch,
    conn_type,
    repositories,
    s3UploadPaths,
    runVerification,
    isRepositoryCoderGenericNonJvmSourcePlatform,
    showMuleRuntimeSelection,
    formIncompleteReason,
    clearDraft
  ])

  useEffect(() => {
    globalEventEmitter.globalOn(AppEvents.SubmitTask, onSubmit)
  }, [onSubmit])

  const renderTaskForms = () => {
    switch (command) {
      case CommandType.QUESTION_ANSWERING:
      case CommandType.AUTODESK_MULE_TO_NODE_JS_MIGRATION: {
        return (
          <MuleSoftToNodeJsForm
            onChange={setTask}
            withExamplePrefilled={isExampleMode}
            initialTaskData={initialTaskData}
          />
        )
      }
      case CommandType.MULTI_REPO_QA: {
        const initialCommandString = initialTaskData?.request?.command?.commandString
        return (
          <BaseTaskForm
            enter2Submit
            label='Initial message'
            onChange={setTask}
            placeholder='Type a message...'
            defaultValue={initialCommandString}
            disabled={isExampleMode}
          />
        )
      }
      case CommandType.GENERATE_MAPPING_TABLE: {
        const initialDataweaveScript = initialTaskData?.request?.command?.commandString
        return (
          <MappingTableGeneratorForm
            onChange={setTask}
            withExamplePrefilled={isExampleMode}
            defaultValue={initialDataweaveScript}
          />
        )
      }
      case CommandType.GENERATE_DOCUMENTATION: {
        const initialFlowNames = initialTaskData?.request?.command?.commandString || flownamesString
        return (
          <FlowDocsGeneratorForm
            onChange={setTask}
            flowNames={initialFlowNames}
            disabled={isExampleMode}
          />
        )
      }
      case CommandType.MULE_3_TO_MULE_4_MIGRATION: {
        const initialFlowNames = initialTaskData?.request?.command?.commandString || flownamesString
        return (
          <Mule3ToMule4MigrationForm
            onChange={setTask}
            flowNames={initialFlowNames}
            disabled={isExampleMode}
          />
        )
      }
      case CommandType.GENERATE_IMAGE: {
        const generateFlowDiag = initialTaskData?.request?.generateFlowDiag
        const initialDiagramType = generateFlowDiag ? 'flow' : 'sequence'

        return (
          <DiagramGeneratorForm
            onChange={setTask}
            setIsMultiSelect={setIsMultiSelect}
            withExamplePrefilled={isExampleMode}
            initialDiagramType={initialDiagramType}
          />
        )
      }
      case CommandType.GENERATE_ADD_FLOW_CODE_PR:
      case CommandType.GENERATE_REPO_ADD_FLOW_CODE_PR: {
        return (
          <IntegrationGeneratorForm
            onChange={setTask}
            withExamplePrefilled={isExampleMode}
            initialTaskData={initialTaskData}
          />
        )
      }
      case CommandType.GENERATE_DATAWEAVE:
      case CommandType.GENERATE_DATAWEAVE_REPO_AWARE:
        return (
          <MappingTableToDWLForm
            onChange={setTask}
            withExamplePrefilled={isExampleMode}
            initialTaskData={initialTaskData}
          />
        )
      case CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION: {
        const sourceExample = initialTaskData?.request?.sourceOutputExamples?.[0]
        const convertedInputType = sourceExample?.sourceDataType
          ? sourceOutputExampleTypeToJSON(sourceExample.sourceDataType)
          : undefined

        return (
          <InputOutputToDWLForm
            onChange={setTask}
            withExamplePrefilled={isExampleMode}
            inputDefaultValue={sourceExample?.sourceData}
            outputDefaultValue={sourceExample?.outputData}
            inputTypeDefaultValue={convertedInputType}
            initInputOutputNotes={initialTaskData?.request?.inputOutputNotes || ''}
          />
        )
      }
      case CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION_REPO_AWARE: {
        const sourceExample = initialTaskData?.request?.sourceOutputExamples?.[0]
        const convertedInputType = sourceExample?.sourceDataType
          ? sourceOutputExampleTypeToJSON(sourceExample.sourceDataType)
          : undefined

        return (
          <InputOutputToDWLForm
            onChange={setTask}
            vmAware
            withExamplePrefilled={isExampleMode}
            initVmArgs={taskRuntimeSettings.vm_args || ''}
            suggestedVmArgs={suggestedVmArgs}
            isLoadingSuggestedVmArgs={shouldShowLoading}
            inputDefaultValue={sourceExample?.sourceData}
            outputDefaultValue={sourceExample?.outputData}
            inputTypeDefaultValue={convertedInputType}
            initInputOutputNotes={initialTaskData?.request?.inputOutputNotes || ''}
          />
        )
      }
      case CommandType.MIGRATION_DOCUMENT:
        return (
          <GenerateMigrationDocForm
            onChange={setTask}
            withExamplePrefilled={isExampleMode}
            initialTaskData={initialTaskData}
          />
        )
      case CommandType.MUNIT_TEST_CREATION: {
        const originalRequest = initialTaskData?.request
        const initVmArgs = originalRequest?.vmArgs || taskRuntimeSettings.vm_args || ''
        const initMunitNotes = originalRequest?.munitNotes || ''
        const initEnableCoverage = originalRequest?.munitEnableCoverage ?? true
        const initNumOfScenarios = originalRequest?.munitNumScenarios || 1
        const flowName = originalRequest?.command?.commandString || flownamesString

        return (
          <MunitTestForm
            onChange={setTask}
            flowName={flowName}
            initVmArgs={initVmArgs}
            suggestedVmArgs={suggestedVmArgs}
            isLoadingSuggestedVmArgs={shouldShowLoading}
            hideVmArgs={option === 'none'}
            initMunitNotes={initMunitNotes}
            initEnableCoverage={initEnableCoverage}
            initNumOfScenarios={initNumOfScenarios}
            disabled={isExampleMode}
          />
        )
      }
      case CommandType.DATAWEAVE_TO_DATA_CREATION: {
        const commandString = initialTaskData?.request?.command?.commandString || ''
        const dwlExample = initialTaskData?.request?.dwlToInputOutputExample
        const dataweaveScript = dwlExample?.commandString || commandString
        const dataType = dwlExample?.exampleType
          ? sourceOutputExampleTypeFromJSON(dwlExample.exampleType)
          : SourceOutputExampleType.DATA_TYPE_JSON
        const notes = initialTaskData?.request?.userNotes || ''

        return (
          <SampleDataGeneratorForm
            onChange={setTask}
            withExamplePrefilled={isExampleMode}
            dataweaveDefaultValue={dataweaveScript}
            exampleTypeDefaultValue={dataType}
            initNotes={notes}
          />
        )
      }
      case CommandType.DATAWEAVE_TO_DATA_CREATION_REPO_AWARE: {
        const commandString = initialTaskData?.request?.command?.commandString || ''
        const dwlExample = initialTaskData?.request?.dwlToInputOutputExample
        const dataweaveScript = dwlExample?.commandString || commandString
        const dataType = dwlExample?.exampleType
          ? sourceOutputExampleTypeFromJSON(dwlExample.exampleType)
          : SourceOutputExampleType.DATA_TYPE_JSON
        const notes = initialTaskData?.request?.userNotes || ''

        return (
          <SampleDataGeneratorForm
            onChange={setTask}
            withExamplePrefilled={isExampleMode}
            vmAware
            initVmArgs={taskRuntimeSettings.vm_args || ''}
            suggestedVmArgs={suggestedVmArgs}
            isLoadingSuggestedVmArgs={shouldShowLoading}
            hideVmArgs={option === 'none'}
            dataweaveDefaultValue={dataweaveScript}
            exampleTypeDefaultValue={dataType}
            initNotes={notes}
          />
        )
      }
      case CommandType.GENERATE_SIMPLE_CODE_PR: {
        const hideVmArgs = option === 'none' || isRepositoryCoderGenericNonJvmSourcePlatform
        return (
          <CodeEnhancerForm
            onChange={setTask}
            withExamplePrefilled={isExampleMode}
            initVmArgs={taskRuntimeSettings.vm_args || ''}
            suggestedVmArgs={suggestedVmArgs}
            isLoadingSuggestedVmArgs={shouldShowLoading}
            hideVmArgs={hideVmArgs}
            initialTaskData={initialTaskData}
            disabled={isExampleMode}
            runVerificationDisabled={false}
            runVerification={runVerification}
            onRunVerificationChange={setRunVerification}
          />
        )
      }
      case CommandType.MULESOFT_TO_CAMEL_MIGRATION: {
        return (
          <MulesoftToCamelForm
            onChange={setTask}
            disabled={isExampleMode}
            initialTaskData={initialTaskData}
          />
        )
      }
      case CommandType.BILLABLE_FLOW_DOCUMENTATION: {
        return (
          <BillableMuleFlowForm
            onChange={setTask}
            disabled={isExampleMode}
            initialTaskData={initialTaskData}
          />
        )
      }
      case CommandType.MIGRATION_ACCELERATOR: {
        return (
          <MigrationAcceleratorForm
            onChange={setTask}
            disabled={isExampleMode}
            initialTaskData={initialTaskData}
          />
        )
      }
      case CommandType.GENERATE_MULTI_REPO_FLOW_DIAGRAM: {
        const initialDescription = initialTaskData?.request?.userNotes || ''

        return (
          <BaseTaskForm
            onChange={setTask}
            defaultValue={initialDescription}
            label='Description'
            placeholder='Describe the flow diagram requirements...'
            minRows={4}
            disabled={isExampleMode}
          />
        )
      }
      case CommandType.INTEGRATION_TEST: {
        // Integration test uses the multi-step flow (IntegrationTestFlowScreen)
        // Return null to hide the old form
        return null
      }
      case CommandType.MULE_VERSION_UPGRADE: {
        return (
          <MuleVersionUpgradeForm
            onChange={setTask}
            withExamplePrefilled={isExampleMode}
            initVmArgs={taskRuntimeSettings.vm_args || ''}
            suggestedVmArgs={suggestedVmArgs}
            isLoadingSuggestedVmArgs={shouldShowLoading}
            hideVmArgs={option === 'none'}
            initialTaskData={initialTaskData}
            disabled={isExampleMode}
            runVerificationDisabled={false}
            runVerification={runVerification}
            onRunVerificationChange={setRunVerification}
          />
        )
      }
      default: {
        const initialCommandString =
          draft?.description ?? initialTaskData?.request?.command?.commandString
        // Azure Migrate + Deploy uses the selected repository as its real input;
        // the description is optional context, so don't mark it required.
        const isDescriptionOptional = command === CommandType.AZURE_DEPLOY_TEST
        return (
          <BaseTaskForm
            onChange={setTask}
            defaultValue={initialCommandString}
            disabled={isExampleMode}
            required={!isDescriptionOptional}
          />
        )
      }
    }
  }

  const isAgentUnavailable = useAgentUnavailable()

  const isFormIncomplete = Boolean(formIncompleteReason)
  const submitDisabled = disabled || isAgentUnavailable || isFormIncomplete

  const generateButtonGroup = () => {
    return (
      <div
        className={cm(
          'button-groups flex items-center justify-center text-sm',
          isPlugin ? 'mt-3' : 'mt-6'
        )}
      >
        <Button
          variant='outlined'
          onClick={closeTask}
          className='mx-2 rounded-xl'
          aria-label='Cancel Task Form'
          data-testid='task-cancel-button'
        >
          Cancel
        </Button>
        <Button
          variant='primary'
          onClick={onSubmit}
          className='submit-step'
          aria-label='Submit Task Form'
          isDisabled={submitDisabled}
          data-testid='task-submit-button'
        >
          {QuestionAnsweringCommands.includes(command!) ? 'Start' : 'Submit'}
        </Button>
      </div>
    )
  }

  // Use a regular function (not useCallback) to avoid stale closures
  const handleIntegrationRepoSave = (payload: {
    option: ProjectSelectionOption
    repository?: { conn_type: string; repository: string; branch: string }
    runtimeSettings?: Record<string, string | undefined>
    s3Path?: string
  }) => {
    if (payload.option === 'codeContext' && payload.repository) {
      handleOptionChange('codeContext')
      setRepositories([payload.repository])
      if (payload.runtimeSettings) {
        setTaskRuntimeSettings(payload.runtimeSettings)
      }
      // Dispatch Redux action to complete repository selection and move to configureFlows step
      dispatch(
        completeRepositorySelection({
          repository: payload.repository,
          runtimeSettings: payload.runtimeSettings
        })
      )
      // Close modal AFTER state update to prevent race condition
      setIntegrationRepoModalOpen(false)
      return
    }

    if (payload.option === 'upload' && payload.s3Path) {
      setOption('upload') // Update option directly without side effects
      setRepositories([])
      setS3UploadPaths([payload.s3Path])
      // Dispatch Redux action to complete repository selection with S3 path
      dispatch(
        completeRepositorySelection({
          repository: {
            conn_type: 'S3',
            repository: '',
            branch: 'main',
            s3TarFilePath: payload.s3Path
          },
          runtimeSettings: {}
        })
      )
      // Close modal AFTER state update to prevent race condition
      setIntegrationRepoModalOpen(false)
      return
    }
  }

  const generateProjectSelection = () => {
    // Mapping Table Generator
    if (command === CommandType.GENERATE_MAPPING_TABLE) {
      return null
    }

    if (command === CommandType.INTEGRATION_TEST) {
      const primaryRepo = repositories[0]
      const uploadedPath =
        s3UploadPaths[0] || (initialRepositories.length > 0 ? initialRepositories[0].s3Path : '')
      const disableSelection =
        isExampleMode ||
        (isEditingTask && (repositories.length > 0 || initialRepositories.length > 0))
      const hasSelection =
        option === 'upload' ? Boolean(uploadedPath) : Boolean(primaryRepo && primaryRepo.branch)
      const summaryLabel = option === 'upload' ? 'Uploaded Repo' : 'Repository'

      return (
        <>
          <div className='mt-2 rounded-2xl border border-default-200 bg-white px-5 py-4 flex items-start justify-between gap-4'>
            <div className='space-y-2'>
              <div className='text-xs font-semibold uppercase tracking-wide text-default-500 select-none'>
                {summaryLabel}
              </div>
              {option === 'codeContext' && primaryRepo ? (
                <>
                  <div className='text-sm font-medium text-default-800 select-none'>
                    {primaryRepo.repository}
                  </div>
                  <div className='text-xs text-default-500 select-none'>
                    Branch: {primaryRepo.branch}
                  </div>
                </>
              ) : option === 'upload' && uploadedPath ? (
                <>
                  <div className='text-sm font-medium text-default-800 break-all select-none'>
                    {uploadedPath.split('/').pop() || uploadedPath}
                  </div>
                  <div className='text-xs text-default-500 break-all select-none'>
                    {uploadedPath}
                  </div>
                </>
              ) : (
                <div className='text-sm text-default-500 select-none'>No repository selected</div>
              )}
            </div>
            <Button
              variant='outlined'
              onClick={() => {
                dispatch(startFlow())
                setIntegrationRepoModalOpen(true)
              }}
              isDisabled={disableSelection}
              className='h-10 px-5'
            >
              {hasSelection ? 'Change' : 'Select'}
            </Button>
          </div>

          <IntegrationRepoSelectionModal
            isOpen={isIntegrationRepoModalOpen}
            onClose={() => {
              setIntegrationRepoModalOpen(false)
              // Reset flow to idle when user cancels modal without saving
              if (currentFlowStep === 'selectRepository') {
                dispatch(resetFlow())
              }
            }}
            command={command as UsedCommandType}
            initialOption={option}
            repositories={repositories}
            initialS3Path={uploadedPath}
            taskRuntimeSettings={taskRuntimeSettings}
            onSave={handleIntegrationRepoSave}
            disabled={disableSelection}
          />
        </>
      )
    }

    // Migration Accelerator supports both 'codeContext' and 'upload'
    if (command === CommandType.MIGRATION_ACCELERATOR) {
      if (option === 'upload') {
        return (
          <>
            <div className='text-sm font-semibold text-default-700 mb-1 select-none'>
              {isMultiRepoRequired(command) ? (
                <>
                  Upload Projects:<span className='text-tokens-status-error-text'> *</span>
                </>
              ) : (
                <>
                  Upload Project:<span className='text-tokens-status-error-text'> *</span>
                </>
              )}
            </div>
            {renderUploadInput()}
          </>
        )
      }
      return (
        <CodeContextSelector
          data-testid='code-context-selector'
          setTaskRuntimeSettings={setTaskRuntimeSettings}
          command={command as UsedCommandType}
          repositories={repositories}
          setRepositories={setRepositories}
          disabled={isExampleMode || (isEditingTask && repositories.length > 0)}
        />
      )
    }

    switch (option) {
      case 'upload': {
        return (
          <>
            <div className='text-sm font-semibold text-default-700 mb-1 select-none'>
              {isMultiRepoRequired(command) ? (
                <>
                  Upload Projects:<span className='text-tokens-status-error-text'> *</span>
                </>
              ) : (
                <>
                  Upload Project:<span className='text-tokens-status-error-text'> *</span>
                </>
              )}
            </div>
            {renderUploadInput()}
          </>
        )
      }
      case 'codeContext': {
        return (
          <CodeContextSelector
            setTaskRuntimeSettings={setTaskRuntimeSettings}
            command={command as UsedCommandType}
            repositories={repositories}
            setRepositories={setRepositories}
            disabled={isExampleMode || (isEditingTask && repositories.length > 0)}
          />
        )
      }
      default:
        return null
    }
  }

  const renderRadioGroup = () => {
    if (command === CommandType.GENERATE_MAPPING_TABLE) {
      return null
    }
    if (
      [
        CommandType.GENERATE_ADD_FLOW_CODE_PR,
        CommandType.GENERATE_REPO_ADD_FLOW_CODE_PR,
        CommandType.GENERATE_SIMPLE_CODE_PR
      ].includes(command as CommandType)
    ) {
      return (
        <RadioGroup
          id='project-selection'
          options={[
            { label: 'No Repository', value: 'none' },
            { label: 'With Repository', value: 'codeContext' },
            { label: 'Upload from computer', value: 'upload' }
          ]}
          defaultValue={option}
          onChange={(ev: ChangeEvent<HTMLInputElement>) => {
            const selection = ev.target.value as ProjectSelectionOption
            handleOptionChange(selection)
          }}
          className='my-4'
          isDisabled={isExampleMode || isEditingTask}
        />
      )
    }
    if (
      [
        CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION,
        CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION_REPO_AWARE,
        CommandType.GENERATE_DATAWEAVE
      ].includes(command as CommandType)
    ) {
      return (
        <RadioGroup
          id='project-selection'
          options={[
            { label: 'No Repository', value: 'none' },
            { label: 'With Repository', value: 'codeContext' },
            { label: 'Upload from computer', value: 'upload' },
            { label: 'With Mapping Table', value: 'mappingTable' }
          ]}
          defaultValue={option}
          onChange={(ev: ChangeEvent<HTMLInputElement>) => {
            const selection = ev.target.value as ProjectSelectionOption
            handleOptionChange(selection)
          }}
          className='my-4'
          isDisabled={isExampleMode || isEditingTask}
        />
      )
    }
    if (
      [
        CommandType.DATAWEAVE_TO_DATA_CREATION,
        CommandType.DATAWEAVE_TO_DATA_CREATION_REPO_AWARE
      ].includes(command as CommandType)
    ) {
      return (
        <RadioGroup
          id='project-selection'
          options={[
            { label: 'No Repository', value: 'none' },
            { label: 'With Repository', value: 'codeContext' },
            { label: 'Upload from computer', value: 'upload' }
          ]}
          defaultValue={option}
          onChange={(ev: ChangeEvent<HTMLInputElement>) => {
            const selection = ev.target.value as ProjectSelectionOption
            handleOptionChange(selection)
          }}
          className='my-4'
          isDisabled={isExampleMode || isEditingTask}
        />
      )
    }
    if (command === CommandType.MIGRATION_ACCELERATOR) {
      return (
        <RadioGroup
          id='project-selection'
          options={[
            { label: 'With Repository', value: 'codeContext' },
            { label: 'Upload from computer', value: 'upload' }
          ]}
          defaultValue={option}
          onChange={(ev: ChangeEvent<HTMLInputElement>) => {
            const selection = ev.target.value as ProjectSelectionOption
            handleOptionChange(selection)
          }}
          className='my-4'
          isDisabled={isExampleMode || isEditingTask}
        />
      )
    }
    return (
      <RadioGroup
        id='project-selection'
        options={[
          { label: 'With Repository', value: 'codeContext' },
          { label: 'Upload from computer', value: 'upload' }
        ]}
        defaultValue={option}
        onChange={(ev: ChangeEvent<HTMLInputElement>) => {
          const selection = ev.target.value as ProjectSelectionOption
          handleOptionChange(selection)
        }}
        className='my-4'
        isDisabled={isExampleMode || isEditingTask}
      />
    )
  }

  return (
    <div className={cm('h-full w-full flex flex-col', className)}>
      {/* Hide main form when in integration test flow */}
      {!isInFlow && (
        <>
          <div
            className={cm(
              'flex flex-col gap-1 transition-transform duration-500 ease-in-out',
              !isPlugin && collapsed
                ? 'translate-x-[132px] -translate-y-px'
                : 'translate-x-0 translate-y-0'
            )}
          >
            <div
              className={cm(
                'task-form-header flex items-center pt-4 w-full pb-1',
                isPlugin ? 'pl-3 pr-3 gap-2' : 'pl-[60px] justify-between'
              )}
            >
              {isPlugin && (
                <div
                  className='inline-flex items-center justify-center w-6 h-6 rounded-[5px] bg-[#efefef] cursor-pointer shrink-0'
                  onClick={closeTask}
                >
                  <CaretLeftIcon className='w-4 h-4' />
                </div>
              )}
              {getTitle()}
            </div>
            <div className={cm('taks-subtitle pb-6', isPlugin ? 'pl-3 pr-3' : 'pl-[60px]')}>
              {getDesc()}
            </div>
          </div>
          <div className='h-3 w-full flex-shrink-0'>
            <HeaderGradient />
          </div>
          <div
            className={cm(
              'flex w-full relative',
              isPlugin ? 'flex-1 min-h-0' : 'h-[calc(100vh-100px)]'
            )}
          >
            {!isPlugin && (
              <div className='back-button absolute top-0 left-0 h-12 w-12 flex items-center justify-center'>
                <div
                  className='inline-flex items-center justify-center w-5 h-5 rounded-[5px] bg-[#efefef] cursor-pointer'
                  onClick={closeTask}
                >
                  <CaretLeftIcon className='w-4 h-4' />
                </div>
              </div>
            )}
            <div className='scollable-container w-full h-full overflow-y-auto'>
              <div
                className={cm(
                  'flex justify-center w-full',
                  isPlugin ? 'pt-4 pb-10 px-3' : 'pt-10 pb-20 px-[60px]'
                )}
              >
                <div
                  className={cm(
                    'task-form-body flex-grow pb-5 w-full',
                    isPlugin ? 'max-w-full min-w-0' : 'max-w-[850px] min-w-[600px]'
                  )}
                >
                  {/* set try example checkbox - only show if command has options and examples */}
                  {command &&
                    AgentCommands[command]?.hasOptions !== false &&
                    AgentCommands[command]?.hasExample !== false && (
                      <div className='pb-2 select-none'>
                        <Checkbox
                          isSelected={isTryExample}
                          onChange={e => setIsTryExample(e.target.checked)}
                        >
                          Try Example
                        </Checkbox>
                      </div>
                    )}
                  <div className='pb-5'>{renderRadioGroup()}</div>
                  <div>
                    <div className='space-y-5'>
                      <div>{generateProjectSelection()}</div>
                      <div className='form-step'>{renderTaskForms()}</div>
                    </div>
                    <div
                      className={cm(
                        'pt-6 pb-6',
                        isPlugin ? 'flex flex-col gap-4' : 'flex justify-between'
                      )}
                    >
                      {isHideRuntimeSelect() ? null : (
                        <div
                          className={cm(
                            'flex-none item-start',
                            isPlugin ? 'w-full' : needFlowNameInput ? 'w-[48%]' : 'w-full'
                          )}
                        >
                          {command === CommandType.MULE_VERSION_UPGRADE && (
                            <div className='mb-4'>
                              <RuntimeSelection
                                taskRuntimeSettings={oldRuntimeSettings}
                                setTaskRuntimeSettings={setOldRuntimeSettings}
                                task={null}
                                setTask={() => {}}
                                label={
                                  <>
                                    current runtime settings
                                    <span className='text-tokens-status-error-text'> *</span>
                                  </>
                                }
                                showSettingsXML={false}
                                disabled={isExampleMode}
                                isLoading={shouldShowLoading}
                              />
                            </div>
                          )}
                          <RuntimeSelection
                            data-testid='runtime-selection'
                            taskRuntimeSettings={taskRuntimeSettings}
                            setTaskRuntimeSettings={setTaskRuntimeSettings}
                            task={task}
                            setTask={setTask}
                            label={
                              command === CommandType.MULE_VERSION_UPGRADE ? (
                                <>
                                  target runtime settings
                                  <span className='text-tokens-status-error-text'> *</span>
                                </>
                              ) : (
                                <>
                                  {showMuleRuntimeSelection
                                    ? 'Runtime Settings (Select mvn, java, mule runtime version)'
                                    : 'Runtime Settings (Select mvn and java version)'}
                                  <span className='text-tokens-status-error-text'> *</span>
                                </>
                              )
                            }
                            showSettingsXML={true}
                            showMuleRuntime={showMuleRuntimeSelection}
                            disabled={isExampleMode}
                            isLoading={shouldShowLoading}
                          />
                        </div>
                      )}
                      {needFlowNameInput && (
                        <div
                          className={cm(
                            'flex-none item-end',
                            isPlugin ? 'w-full' : !isHideRuntimeSelect() ? 'w-[48%]' : 'w-full'
                          )}
                        >
                          <FlowNameSelector
                            data-testid='flowname-selector'
                            withExamplePrefilled={isExampleMode}
                            currentTask={task}
                            setCommandString={setCommandString}
                            currentOption={option}
                            command={command as UsedCommandType}
                            onFlowNamesChange={setFlowNamesString}
                            isMultiSelect={isMultiSelect}
                            initialSelectedFlows={
                              initialTaskData?.request?.command?.commandString
                                ? initialTaskData.request.command.commandString
                                    .split(',')
                                    .map(flow => flow.trim())
                                : undefined
                            }
                            flowNames={flowNames}
                            isLoadingFlowNames={isFetchingRepoMetadata}
                            maxFlows={
                              command === CommandType.GENERATE_DOCUMENTATION
                                ? DOCUMENT_GENERATOR_MAX_FLOWS
                                : undefined
                            }
                          />
                        </div>
                      )}
                    </div>

                    {/* Required fields indicator */}
                    <div className='mx-2 mt-2'>
                      <Typography
                        fontSize='bodySm'
                        fontWeight='medium'
                        fontFamily='geist'
                        className='select-none'
                      >
                        Fields marked with a red asterisk (
                        <span className='text-tokens-status-error-text'>*</span>) are required
                      </Typography>
                    </div>
                    <div>{generateButtonGroup()}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Integration Test Flow Screen - shown when in any flow step (configureFlows, deployment, review) */}
      {isInFlow && currentFlowStep !== 'selectRepository' && (
        <IntegrationTestFlowScreen
          onExit={() => {
            // Reset flow and navigate back
            dispatch(resetFlow())
            // Navigate back to workspace home
            if (workspaceContext.currentWorkspace?.uuid) {
              navigate(`/w/${workspaceContext.currentWorkspace.uuid}`)
            }
          }}
          onTaskSubmit={onTaskSubmit}
        />
      )}
    </div>
  )
}
